/**
 * 상품 가격대 구간 경계 — 서버 절과 화면 칩이 함께 읽는 한 벌.
 *
 * 경계는 여기 한 곳에만 있고, **서버는 이 경계로 ES 절을 만들고 화면은 이 경계로 칩
 * 이름을 짓는다.** 표시가(`displayPrice`)가 색인에 없어 절은 서버가 만들어야 하고
 * (할인가 유무로 갈린다) 사람이 읽는 이름은 화면 몫이라, 값 자체만 공유한다.
 *
 * ⚠️ 응답 계약에는 구간 값(`a`~`d`)만 실린다 — 경계를 응답에 실어 화면이 이름을
 * 지어내게 하는 안은 판단 대기다. 넓히지 마라.
 *
 * 구간은 `[min, max)` — `max: null` 은 상한 없음이다.
 */
export type ProductPriceBandSpec = {
  value: string;
  min: number;
  max: number | null;
};

/** 구간 경계 — 배열 순서가 화면 칩·서버 패싯의 순서다 */
export const PRODUCT_PRICE_BAND_SPECS: readonly ProductPriceBandSpec[] = [
  { value: 'a', min: 0, max: 10000 },
  { value: 'b', min: 10000, max: 30000 },
  { value: 'c', min: 30000, max: 50000 },
  { value: 'd', min: 50000, max: null },
];

/** 화면이 고를 수 있는 구간 값 — 패싯이 이 순서로 나간다 */
export const PRODUCT_PRICE_BANDS = PRODUCT_PRICE_BAND_SPECS.map((spec) => spec.value);

/** 값으로 구간을 찾는다 — 모르는 값이면 `null` */
export function findProductPriceBandSpec(value: string): ProductPriceBandSpec | null {
  return PRODUCT_PRICE_BAND_SPECS.find((spec) => spec.value === value) || null;
}

/** 가격이 구간 안인가 — `[min, max)` */
export function isPriceInBand(spec: ProductPriceBandSpec, price: number): boolean {
  return price >= spec.min && (spec.max === null || price < spec.max);
}

/**
 * 구간 이름 — 「1만원 미만」·「1~3만원」·「5만원 이상」.
 *
 * 경계를 만원 단위로 읽어 짓는다. 하한이 0이면 「미만」, 상한이 없으면 「이상」이다.
 */
export function formatProductPriceBandLabel(spec: ProductPriceBandSpec): string {
  const man = (won: number) => won / 10000;
  if (spec.max === null) return `${man(spec.min)}만원 이상`;
  if (spec.min <= 0) return `${man(spec.max)}만원 미만`;
  return `${man(spec.min)}~${man(spec.max)}만원`;
}
